import { LatLng } from '@open-tender/types'
import { geoOptions } from './useGeolocation'

export interface GeolocationResult {
  latLng: LatLng | null
  error: string | null
}

// Also bounds an unanswered permission prompt, which the browser's own timeout does not cover.
export const GEOLOCATION_WAIT = geoOptions.timeout

// Unlike useGeolocation (mount-only), this can be called from an event handler.
const requestGeolocation = async (
  waitMs: number = GEOLOCATION_WAIT
): Promise<GeolocationResult> => {
  const geo = navigator.geolocation
  if (!geo) {
    return { latLng: null, error: 'Geolocation is not supported' }
  }

  // A denied permission can leave getCurrentPosition without a callback, so check first.
  try {
    const permissions = navigator.permissions
    if (permissions) {
      const status = await permissions.query({ name: 'geolocation' })
      if (status.state === 'denied') {
        return { latLng: null, error: 'Geolocation permission denied' }
      }
    }
  } catch (err) {
    // Permissions API unavailable -- fall through to the request itself.
  }

  return new Promise<GeolocationResult>((resolve) => {
    let timer: ReturnType<typeof setTimeout>
    let isSettled = false

    const settle = (result: GeolocationResult) => {
      if (isSettled) return
      isSettled = true
      clearTimeout(timer)
      resolve(result)
    }

    timer = setTimeout(
      () => settle({ latLng: null, error: 'Geolocation request timed out' }),
      waitMs
    )

    geo.getCurrentPosition(
      (position: GeolocationPosition) => {
        const { coords } = position
        settle({
          latLng: { lat: coords.latitude, lng: coords.longitude },
          error: null,
        })
      },
      (error: GeolocationPositionError) => {
        settle({ latLng: null, error: error.message })
      },
      geoOptions
    )
  })
}

export default requestGeolocation
